import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, type ReferenceData, type VehicleDetail } from '../api/client'
import { invalidateFleet } from '../api/invalidate'
import { SearchSelect } from './SearchSelect'
import { siteOptions, vehicleTypeOptions } from './referenceOptions'
import './VehicleFields.css'

/**
 * The vehicle's own fields — read-only until asked, then editable in place.
 *
 * The register's duplicate check offers "open and edit it", and the page it opens
 * could previously only change compliance dates: type, make, year, chassis, asset
 * code and site were plain text with no way in. That made the offer half true,
 * which is worse than not making it.
 *
 * Registration is absent on purpose. Changing it would make this a different
 * vehicle, and the canonical number is the key every other table joins on —
 * correcting a mistyped plate is a merge, not a field edit.
 */
export function VehicleFields({
  vehicle,
  reference,
}: {
  vehicle: VehicleDetail
  reference: ReferenceData | undefined
}) {
  const [editing, setEditing] = useState(false)

  if (!editing) {
    return (
      <>
        <dl className="kv">
          <dt>Type</dt>
          <dd>{vehicle.vehicleType ?? '—'}</dd>
          <dt>Make</dt>
          <dd>{[vehicle.make, vehicle.model].filter(Boolean).join(' ') || '—'}</dd>
          <dt>Reg. year</dt>
          <dd>{vehicle.registrationYear ?? '—'}</dd>
          <dt>Chassis</dt>
          <dd className="mono kv__wrap">{vehicle.chassisNumber ?? '—'}</dd>
          <dt>Engine</dt>
          <dd className="mono kv__wrap">{vehicle.engineNumber ?? '—'}</dd>
          <dt>Asset code</dt>
          <dd className="mono kv__wrap">{vehicle.assetCode ?? '—'}</dd>
          <dt>Site</dt>
          <dd>{vehicle.site ?? 'unassigned'}</dd>
        </dl>

        <button type="button" className="btn vfields__edit" onClick={() => setEditing(true)}>
          Edit these details
        </button>
      </>
    )
  }

  return (
    <VehicleFieldsForm
      vehicle={vehicle}
      reference={reference}
      onDone={() => setEditing(false)}
    />
  )
}

function VehicleFieldsForm({
  vehicle,
  reference,
  onDone,
}: {
  vehicle: VehicleDetail
  reference: ReferenceData | undefined
  onDone: () => void
}) {
  const [vehicleTypeId, setVehicleTypeId] = useState<number | ''>(vehicle.vehicleTypeId ?? '')
  const [siteId, setSiteId] = useState<number | ''>(vehicle.siteId ?? '')
  const [make, setMake] = useState(vehicle.make ?? '')
  const [model, setModel] = useState(vehicle.model ?? '')
  const [chassisNumber, setChassisNumber] = useState(vehicle.chassisNumber ?? '')
  const [engineNumber, setEngineNumber] = useState(vehicle.engineNumber ?? '')
  const [registrationYear, setRegistrationYear] = useState(
    vehicle.registrationYear?.toString() ?? '',
  )
  const [assetCode, setAssetCode] = useState(vehicle.assetCode ?? '')

  const queryClient = useQueryClient()

  const save = useMutation({
    mutationFn: () =>
      api.editVehicle(vehicle.vehicleId!, {
        vehicleTypeId: vehicleTypeId as number,
        make: make || null,
        model: model || null,
        chassisNumber: chassisNumber || null,
        engineNumber: engineNumber || null,
        registrationYear: registrationYear ? Number(registrationYear) : null,
        assetCode: assetCode || null,
        siteId: siteId === '' ? null : (siteId as number),
        // The RowVersion this record was read with. Without it, two people on the
        // same vehicle would silently overwrite each other.
        rowVersion: vehicle.rowVersion!,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['vehicle', vehicle.vehicleId] })
      await invalidateFleet(queryClient)
      await queryClient.invalidateQueries({ queryKey: ['activity', vehicle.vehicleId] })
      onDone()
    },
  })

  const conflict = save.error instanceof ApiError && save.error.isConflict
  const selectedType = reference?.vehicleTypes?.find((t) => t.vehicleTypeId === vehicleTypeId)

  return (
    <form
      className="vfields"
      onSubmit={(e) => {
        e.preventDefault()
        if (vehicleTypeId !== '') save.mutate()
      }}
    >
      <p className="vfields__reg mono">{vehicle.registration}</p>
      <p className="upload__hint">
        The registration number is not editable — changing it would make this a different
        vehicle.
      </p>

      <div className="editor__field">
        <label className="eyebrow" htmlFor="edit-type">
          Type *
        </label>
        <SearchSelect
          id="edit-type"
          options={vehicleTypeOptions(reference)}
          value={vehicleTypeId}
          onChange={(v) => setVehicleTypeId(v)}
          placeholder="Search types…"
        />
      </div>

      <div className="editor__field">
        <label className="eyebrow" htmlFor="edit-site">
          Site
        </label>
        <SearchSelect
          id="edit-site"
          options={siteOptions(reference)}
          value={siteId}
          onChange={(v) => setSiteId(v)}
          emptyLabel="Unassigned"
          placeholder="Search sites…"
        />
      </div>

      <div className="vfields__pair">
        <label className="editor__field">
          <span className="eyebrow">Make</span>
          <input value={make} onChange={(e) => setMake(e.target.value)} />
        </label>

        <label className="editor__field">
          <span className="eyebrow">Model</span>
          <input value={model} onChange={(e) => setModel(e.target.value)} />
        </label>
      </div>

      <label className="editor__field">
        <span className="eyebrow">Chassis no.</span>
        <input
          className="mono"
          value={chassisNumber}
          onChange={(e) => setChassisNumber(e.target.value)}
        />
      </label>

      <label className="editor__field">
        <span className="eyebrow">Engine no.</span>
        <input
          className="mono"
          value={engineNumber}
          onChange={(e) => setEngineNumber(e.target.value)}
        />
      </label>

      <div className="vfields__pair">
        <label className="editor__field">
          <span className="eyebrow">Reg. year</span>
          <input
            type="number"
            min={1950}
            max={2100}
            value={registrationYear}
            onChange={(e) => setRegistrationYear(e.target.value)}
          />
        </label>

        <label className="editor__field">
          <span className="eyebrow">Asset code</span>
          <input className="mono" value={assetCode} onChange={(e) => setAssetCode(e.target.value)} />
        </label>
      </div>

      {selectedType?.isMachinery && (
        <p className="editor__conflict">
          <b>{selectedType.name} is classified as machinery.</b> The Excel importer excludes
          this type — check the classification is right.
        </p>
      )}

      {conflict && (
        <p className="editor__conflict" role="alert">
          <b>Someone else changed this vehicle while you were editing.</b> Reload to see
          their change, then apply yours again. Nothing you typed has been saved.
        </p>
      )}

      {save.error && !conflict && (
        <p className="editor__error" role="alert">
          {(save.error as Error).message}
        </p>
      )}

      <p className="editor__note">
        Emptying a field clears it. Unlike a spreadsheet import, a blank here is taken to
        mean there is no value.
      </p>

      <div className="editor__actions">
        <button
          type="submit"
          className="btn btn--primary"
          disabled={save.isPending || vehicleTypeId === ''}
        >
          {save.isPending ? 'Saving…' : 'Save details'}
        </button>
        <button type="button" className="btn" onClick={onDone} disabled={save.isPending}>
          Cancel
        </button>
      </div>
    </form>
  )
}
